import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useSeo, { ensureJsonLd } from '@/hooks/useSeo';
import { plotArcs, siteBrand } from '@/mocks/manga';

const quickFacts = [
  { icon: 'ri-film-line', title: '核心设定', desc: '辣妹澪因为家庭原因无处可去，被男主直人收留，开启同居生活。' },
  { icon: 'ri-user-heart-line', title: '感情线', desc: '慢热推进，从互相嫌弃到心动告白，甜而不腻、细节动人。' },
  { icon: 'ri-question-line', title: '整体走向', desc: '轻松搞笑的同居日常 + 层层揭开的身世悬念 + 治愈成长。' },
];

export default function Plot() {
  useSeo({
    title: '泡在我家的辣妹剧情主线全梳理 泡在我家的辣妹讲什么',
    description:
      '泡在我家的辣妹故事主线完整梳理：从相遇同住、磨合了解到各自的秘密，再到心动告白与家庭未来。看漫画前先搞懂泡在我家的辣妹讲什么，剧情脉络一目了然。',
    keywords: '泡在我家的辣妹剧情,泡在我家的辣妹故事主线,泡在我家的辣妹讲什么,泡在我家的辣妹设定',
    path: '/plot',
    type: 'article',
  });

  useEffect(() => {
    ensureJsonLd('ld-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dramaquest2.github.io/' },
        { '@type': 'ListItem', position: 2, name: '剧情主线', item: 'https://dramaquest2.github.io/plot' },
      ],
    });
    ensureJsonLd('ld-article', {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: '泡在我家的辣妹剧情主线全梳理',
      description: '按幕梳理泡在我家的辣妹故事主线，从相遇同住到心动告白。',
      inLanguage: 'zh-CN',
      author: { '@type': 'Organization', name: '泡在我家的辣妹剧情资讯站' },
      publisher: { '@type': 'Organization', name: '泡在我家的辣妹剧情资讯站' },
      mainEntityOfPage: 'https://dramaquest2.github.io/plot',
    });
    return () => {
      ['ld-breadcrumb', 'ld-article'].forEach((id) => document.getElementById(id)?.remove());
    };
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <header className="bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16 lg:px-8">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">剧情主线</span>
            </nav>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-book-open-line"></i>
                剧情主线
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
                <i className="ri-film-line"></i>
                全 {plotArcs.length} 幕
              </span>
            </div>
            <h1 className="mt-4 max-w-3xl text-2xl leading-snug text-foreground-950 md:text-4xl">
              《{siteBrand}》剧情主线全梳理
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              一部作品能否看进去，往往取决于一开始是否理清了脉络。这篇剧情主线为你按“幕”拆解整个故事，从辣妹澪住进直人家的那一刻讲起，让你在看漫画之前就清楚它到底在讲什么。
            </p>
          </div>
        </header>

        <section className="bg-background-50 py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {quickFacts.map((f) => (
                <div key={f.title} className="rounded-lg bg-background-100 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className={`${f.icon} text-lg`}></i>
                  </span>
                  <h2 className="mt-3 font-heading text-lg text-foreground-900">{f.title}</h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{f.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 space-y-8">
              {plotArcs.map((arc) => (
                <article key={arc.id} className="overflow-hidden rounded-lg bg-background-100 lg:grid lg:grid-cols-2">
                  <img
                    src={arc.image}
                    alt={`泡在我家的辣妹剧情主线${arc.step}${arc.title}`}
                    className="h-56 w-full object-cover object-top lg:h-full"
                  />
                  <div className="p-6 md:p-8">
                    <span className="inline-flex items-center gap-2 text-xs font-medium text-accent-700">
                      <i className="ri-sparkling-2-line"></i>
                      {arc.step}
                    </span>
                    <h2 className="mt-2 font-heading text-2xl text-foreground-950">{arc.title}</h2>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">{arc.summary}</p>
                    <ul className="mt-5 space-y-2.5">
                      {arc.points.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-sm text-foreground-700">
                          <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>

            <aside className="mt-12 rounded-lg bg-primary-900 p-6 md:p-8">
              <h3 className="font-heading text-xl text-background-50">相关阅读</h3>
              <p className="mt-2 text-sm text-background-200">
                了解了剧情主线之后，不妨再深入看看角色设定与分集细节，把整部作品的脉络补完整。
              </p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/characters"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-400"
                >
                  了解主要角色
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/episodes"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                >
                  查看分集剧情
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}